import React from 'react';
import styles from './AnalystRatingSummary.module.scss';
import { useTranslation } from 'react-i18next';
import { SectionTitle } from '@benzinga/core-ui';
import AnalystRatingsCharts from './AnalystRatingsCharts';
import { KPIBox } from './KPIBox';
import type { AnalystRatingsSummary } from '@benzinga/calendar-manager';
import type { OverviewValues, RatingsByMonthWithCounts } from '../../utils/quote/types';
import { BullBearStatements } from '@benzinga/quotes-manager';

const footnoteLabel = (text: string) => (
  <>
    {text}
    <sup>1</sup>
  </>
);

export const AnalystRatingSummaryBoxes: React.FC<{ ratingsSummary: AnalystRatingsSummary }> = ({ ratingsSummary }) => {
  const { t } = useTranslation('quote');

  if (!ratingsSummary) {
    return null;
  }

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      {ratingsSummary.consensusRating && (
        <KPIBox
          label={footnoteLabel(t('Quote.AnalystRatings.consensus-rating'))}
          value={ratingsSummary.consensusRating}
        />
      )}
      {ratingsSummary.highestPriceTarget && (
        <KPIBox
          label={footnoteLabel(t('Quote.AnalystRatings.highest-price'))}
          value={`$${ratingsSummary.highestPriceTarget}`}
        />
      )}
      {ratingsSummary.lowestPriceTarget && (
        <KPIBox
          label={footnoteLabel(t('Quote.AnalystRatings.lowest-price-target'))}
          value={`$${ratingsSummary.lowestPriceTarget}`}
        />
      )}
      {ratingsSummary.consensusPriceTarget && (
        <KPIBox
          label={footnoteLabel(t('Quote.AnalystRatings.consensus-price-target'))}
          value={`$${ratingsSummary.consensusPriceTarget}`}
        />
      )}
    </div>
  );
};

export const AnalystRatingSummary: React.FC<{
  ratingsSummary?: AnalystRatingsSummary | null;
  ratingsByMonthWithCounts?: RatingsByMonthWithCounts[];
}> = ({ ratingsSummary, ratingsByMonthWithCounts }) => {
  const { t } = useTranslation('quote');

  if (!ratingsSummary) {
    return null;
  }

  return (
    <div className={`flex flex-col gap-y-8 mt-8 ${styles.analystRatingSummaryContainer}`}>
      {ratingsByMonthWithCounts && (
        <div className="flex flex-col w-full lg:w-9/12">
          <AnalystRatingsCharts
            averageRating={ratingsSummary.consensusRatingValue}
            ratings={ratingsSummary.uniqueRatingsFromLastThreeYears}
            ratingsByMonthWithCounts={ratingsByMonthWithCounts}
          />
        </div>
      )}
      {ratingsSummary.mostRecentFirms?.length > 0 && (
        <div className={styles.firmRecommendationsContainer}>
          <SectionTitle level={4} size="xl" uppercase={false}>
            {t('Quote.AnalystRatings.analyst-firms-making-recommendations')}
            <sup>1</sup>
          </SectionTitle>
          <div className="flex flex-wrap items-center gap-4 mt-2">
            {ratingsSummary.mostRecentFirms.map(analyst => (
              <div className="card flex items-center justify-center font-extrabold" key={analyst.name}>
                {analyst.name}
              </div>
            ))}
          </div>
          {(ratingsSummary?.aggregateRatings || ratingsSummary?.mostRecentFirms?.length) && (
            <>
              <p className="explain-asterisk text-xs p-2">
                <sup>1</sup>
                {t('Quote.AnalystRatings.calculated-from-analyst-ratings')}
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
};

